import { createContext, useContext, useEffect, useRef } from "react";

/**
 * useEscapeKey — dismiss the TOPMOST open layer when Escape is pressed.
 *
 * Why this is shared instead of a `keydown` effect per component: every layer
 * that wrote its own listener got the key at the same time, so Escape inside a
 * modal-on-modal (the user picker opened from the competition editor, an
 * OverflowMenu opened inside a dialog) closed BOTH at once. `RouteModal` had to
 * carry an explicit `&& !profileUid` guard to work around exactly that.
 *
 * "Topmost" is NOT mount order. React runs effects child-first, so a modal
 * nested inside another registers BEFORE its parent — last-in-wins would hand
 * the key to the layer underneath. So each entry carries the nesting depth from
 * `EscapeDepthContext`, which `ModalBackdrop` deepens for its children, and the
 * key goes to the deepest layer; equal depth (two modals opened one after the
 * other) falls back to whichever registered later, which is the one on top.
 *
 * The document listener is attached while at least one layer is registered and
 * removed when the last one goes, so an app with nothing open carries no key
 * handler at all.
 *
 * @param {function(KeyboardEvent):void} handler - runs on Escape while topmost
 * @param {boolean} [enabled=true] - false registers nothing, so a disabled
 *   layer never shadows the one below it
 */

/** How deep in the modal stack the current subtree sits. Provided by
 *  `ModalBackdrop`; anything outside a modal is at 0. */
export const EscapeDepthContext = createContext(0);

const layers = [];
let seq = 0;
let listening = false;

function topLayer() {
  let top = null;
  for (const l of layers) {
    if (!top || l.depth > top.depth || (l.depth === top.depth && l.seq > top.seq)) top = l;
  }
  return top;
}

function onKeyDown(e) {
  if (e.key !== "Escape") return;
  topLayer()?.ref.current?.(e);
}

export function useEscapeKey(handler, enabled = true) {
  const depth = useContext(EscapeDepthContext);
  // The handler is read through a ref so a caller passing an inline arrow
  // (almost all of them) doesn't re-register on every render.
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; });

  useEffect(() => {
    if (!enabled) return undefined;
    const entry = { depth, seq: ++seq, ref: handlerRef };
    layers.push(entry);
    if (!listening) {
      document.addEventListener("keydown", onKeyDown);
      listening = true;
    }
    return () => {
      const i = layers.indexOf(entry);
      if (i !== -1) layers.splice(i, 1);
      if (layers.length === 0 && listening) {
        document.removeEventListener("keydown", onKeyDown);
        listening = false;
      }
    };
  }, [enabled, depth]);
}
